import React from 'react';
import { Bar, Line } from 'react-chartjs-2';
import { useSuperQuery } from '@vadiun/react-hooks-legacy';
import { appUserRatingsService } from '../services/appUserRatingsService';
import { AppUserRatingsFilters, ClientRankingItem } from '../models/appUserRatingsModels';
import { CircularProgress } from '@material-ui/core';

interface Props {
  filters: AppUserRatingsFilters;
  onFiltersChange: (filters: AppUserRatingsFilters) => void;
}

const POSITIVE_COLOR = '#22c55e';
const NEUTRAL_COLOR = '#f59e0b';
const NEGATIVE_COLOR = '#ef4444';
const PRIMARY_COLOR = '#0e7490';

const scoreLabel = (score: number) => `★${score}`;

const barOptions = {
  responsive: true,
  plugins: { legend: { display: false } },
  scales: { y: { beginAtZero: true, ticks: { stepSize: 1 } } }
};

const lineOptions = {
  responsive: true,
  maintainAspectRatio: false,
  plugins: { legend: { display: false } },
  scales: { y: { min: 0, max: 5 } }
};

const rankingOptions = {
  indexAxis: 'y' as const,
  responsive: true,
  maintainAspectRatio: false,
  plugins: { legend: { display: false } },
  scales: {
    x: { min: 0, max: 5, ticks: { stepSize: 1 } }
  }
};

const buildRankingData = (items: ClientRankingItem[]) => ({
  labels: items.map((r) => r.client_name),
  datasets: [
    {
      label: 'Promedio',
      data: items.map((r) => r.average_score),
      backgroundColor: items.map((r) =>
        r.average_score >= 4 ? POSITIVE_COLOR : r.average_score >= 3 ? NEUTRAL_COLOR : NEGATIVE_COLOR
      )
    }
  ]
});

const AppUserRatingsStatsPanel = ({ filters, onFiltersChange }: Props) => {
  const service = appUserRatingsService();

  const statsQuery = useSuperQuery(
    () => service.getStats(filters),
    { showSpinner: false },
    [
      JSON.stringify({
        ...filters,
        dateFrom: filters.dateFrom?.toISOString() ?? null,
        dateTo: filters.dateTo?.toISOString() ?? null
      })
    ]
  );

  const isPositiveActive = filters.score_min === 4 && filters.score_max === null;
  const isNeutralActive = filters.score_min === 3 && filters.score_max === 3;
  const isNegativeActive = filters.score_min === null && filters.score_max === 2;

  const clearScoreFilters = { score_min: null, score_max: null };

  const handlePositive = () =>
    onFiltersChange({
      ...filters,
      ...(isPositiveActive ? clearScoreFilters : { score_min: 4, score_max: null })
    });

  const handleNeutral = () =>
    onFiltersChange({
      ...filters,
      ...(isNeutralActive ? clearScoreFilters : { score_min: 3, score_max: 3 })
    });

  const handleNegative = () =>
    onFiltersChange({
      ...filters,
      ...(isNegativeActive ? clearScoreFilters : { score_min: null, score_max: 2 })
    });

  if (statsQuery.isLoading) {
    return (
      <div className="flex justify-center items-center py-10">
        <CircularProgress size={32} />
      </div>
    );
  }

  const stats = statsQuery.data;
  if (!stats) return null;

  const total = stats.total || 1;
  const positivePct = Math.round((stats.positive / total) * 100);
  const neutralPct = Math.round((stats.neutral / total) * 100);
  const negativePct = Math.round((stats.negative / total) * 100);

  const distributionData = {
    labels: stats.score_distribution.map((d) => scoreLabel(d.score)),
    datasets: [
      {
        label: 'Calificaciones',
        data: stats.score_distribution.map((d) => d.count),
        backgroundColor: stats.score_distribution.map((d) =>
          d.score >= 4 ? POSITIVE_COLOR : d.score === 3 ? NEUTRAL_COLOR : NEGATIVE_COLOR
        )
      }
    ]
  };

  const overTimeData = {
    labels: stats.over_time.map((o) => o.period),
    datasets: [
      {
        label: 'Promedio',
        data: stats.over_time.map((o) => o.average_score),
        borderColor: PRIMARY_COLOR,
        backgroundColor: PRIMARY_COLOR + '33',
        tension: 0.3,
        pointRadius: 4,
        fill: true
      }
    ]
  };

  return (
    <div className="px-8 py-6 flex flex-col gap-6">
      <div className="grid grid-cols-2 gap-4 sm:grid-cols-5">
        <KpiCard label="Total" value={stats.total.toString()} color="#00163B" />
        <KpiCard label="Promedio" value={stats.average_score != null ? `${stats.average_score.toFixed(2)} ★` : 'N/A'} color={PRIMARY_COLOR} />
        <KpiCard
          label="Positivas"
          value={`${stats.positive}`}
          sub={`${positivePct}%`}
          color={POSITIVE_COLOR}
          isActive={isPositiveActive}
          onClick={handlePositive}
        />
        <KpiCard
          label="Neutrales"
          value={`${stats.neutral}`}
          sub={`${neutralPct}%`}
          color={NEUTRAL_COLOR}
          isActive={isNeutralActive}
          onClick={handleNeutral}
        />
        <KpiCard
          label="Negativas"
          value={`${stats.negative}`}
          sub={`${negativePct}%`}
          color={NEGATIVE_COLOR}
          isActive={isNegativeActive}
          onClick={handleNegative}
        />
      </div>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        <ChartCard title="Distribución de puntuaciones">
          <Bar data={distributionData} options={barOptions} />
        </ChartCard>

        <ChartCard title="Promedio mensual">
          <div style={{ height: 260 }}>
            <Line data={overTimeData} options={lineOptions} />
          </div>
        </ChartCard>
      </div>

      {(stats.rankings?.business?.length > 0 || stats.rankings?.particular?.length > 0) && (
        <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
          {stats.rankings?.business?.length > 0 && (
            <ChartCard title="Ranking empresas">
              <div style={{ height: 260, overflowY: 'auto' }}>
                <div style={{ height: Math.max(260, stats.rankings.business.length * 36), position: 'relative' }}>
                  <Bar data={buildRankingData(stats.rankings.business)} options={rankingOptions} />
                </div>
              </div>
            </ChartCard>
          )}
          {stats.rankings?.particular?.length > 0 && (
            <ChartCard title="Ranking particulares">
              <div style={{ height: 260, overflowY: 'auto' }}>
                <div style={{ height: Math.max(260, stats.rankings.particular.length * 36), position: 'relative' }}>
                  <Bar data={buildRankingData(stats.rankings.particular)} options={rankingOptions} />
                </div>
              </div>
            </ChartCard>
          )}
        </div>
      )}
    </div>
  );
};

const KpiCard = ({
  label,
  value,
  sub,
  color,
  isActive,
  onClick
}: {
  label: string;
  value: string;
  sub?: string;
  color: string;
  isActive?: boolean;
  onClick?: () => void;
}) => (
  <div
    onClick={onClick}
    className="rounded-xl p-4 flex flex-col gap-1 text-white transition-all"
    style={{
      backgroundColor: color,
      cursor: onClick ? 'pointer' : 'default',
      outline: isActive ? '3px solid white' : 'none',
      outlineOffset: '2px',
      opacity: isActive === false && onClick ? 0.7 : 1
    }}
  >
    <span className="text-xs opacity-75 uppercase tracking-wide">{label}</span>
    <span className="text-2xl font-bold leading-tight">{value}</span>
    {sub && <span className="text-xs opacity-75">{sub}</span>}
    {onClick && (
      <span className="text-xs opacity-50 mt-1">
        {isActive ? '✕ quitar filtro' : 'filtrar'}
      </span>
    )}
  </div>
);

const ChartCard = ({ title, children }: { title: string; children: React.ReactNode }) => (
  <div className="rounded-xl bg-white p-4 shadow-sm">
    <p className="text-sm font-semibold text-gray-600 mb-3">{title}</p>
    {children}
  </div>
);

export default AppUserRatingsStatsPanel;
